// @ts-ignore (gifuct-js)
import { decompressFrames, parseGIF } from "gifuct-js";
// @ts-ignore (gifenc)
import { GIFEncoder, applyPalette, quantize } from "gifenc";

export interface CropRect {
  /** All four in the SOURCE image's natural pixel space, not screen/CSS px. */
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface CropResult {
  file: File;
  blob: Blob;
  dataUrl?: string;
  width: number;
  height: number;
}

export interface CropOptions {
  /** Defaults to the source file's own MIME type */
  outputType?: string;
  outputQuality?: number;
  maxSize?: number;
  /** Include CropResult.dataUrl. Off by default. */
  includeDataUrl?: boolean;
}

export class CropSizeError extends Error {
  constructor(
    public readonly actualSize: number,
    public readonly maxSize: number,
  ) {
    super(
      `Cropped file (${actualSize} bytes) exceeds max size (${maxSize} bytes)`,
    );
    this.name = "CropSizeError";
  }
}

export function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () =>
      reject(new Error(`cropProcessor: failed to load image from ${src}`));
    img.src = src;
  });
}

/**
 * Crop `rect` (in the image's own natural pixel space) out of `img` and
 * encode it as a File, preserving the source's filename/MIME by default.
 */
export async function cropImage(
  img: HTMLImageElement,
  rect: CropRect,
  sourceFile: Pick<File, "name" | "type">,
  options: CropOptions = {},
): Promise<CropResult> {
  const type = options.outputType ?? sourceFile.type ?? "image/png";
  const quality = options.outputQuality;

  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(rect.w));
  canvas.height = Math.max(1, Math.round(rect.h));
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("cropProcessor: failed to get canvas context");

  ctx.drawImage(
    img,
    rect.x,
    rect.y,
    rect.w,
    rect.h,
    0,
    0,
    canvas.width,
    canvas.height,
  );

  const blob: Blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) =>
        b ? resolve(b) : reject(new Error("cropProcessor: toBlob failed")),
      type,
      quality,
    );
  });

  if (options.maxSize && blob.size > options.maxSize) {
    throw new CropSizeError(blob.size, options.maxSize);
  }

  const file = new File([blob], sourceFile.name, { type: blob.type });
  const dataUrl = options.includeDataUrl
    ? canvas.toDataURL(type, quality)
    : undefined;

  return { file, blob, dataUrl, width: canvas.width, height: canvas.height };
}

/* ---------------------------------------------------------------------------
 * Majalis: animated GIF cropping
 * Decode every frame, crop each one, re-encode as an animated GIF.
 * ------------------------------------------------------------------------- */

const GIF_MAX_SIDE = 800;
const GIF_SCALES = [1, 0.75, 0.55, 0.4];

/* eslint-disable @typescript-eslint/no-explicit-any */
export async function cropGif(
  src: string,
  rect: CropRect,
  sourceFile: Pick<File, "name" | "type">,
  options: CropOptions = {},
): Promise<CropResult> {
  const buf = await (await fetch(src)).arrayBuffer();
  const gif: any = parseGIF(buf);
  const frames: any[] = decompressFrames(gif, true);
  if (frames.length === 0) throw new Error("cropGif: the GIF has no frames");

  const W: number = gif.lsd.width;
  const H: number = gif.lsd.height;
  const sx = Math.max(0, Math.min(W - 1, Math.round(rect.x)));
  const sy = Math.max(0, Math.min(H - 1, Math.round(rect.y)));
  const cw = Math.max(1, Math.min(W - sx, Math.round(rect.w)));
  const ch = Math.max(1, Math.min(H - sy, Math.round(rect.h)));

  // very long GIFs: keep every other frame (delays are added together)
  const step = frames.length > 120 ? 2 : 1;
  const tick = () => new Promise<void>((r) => setTimeout(r, 0));

  async function encodeAt(factor: number) {
    const scale = Math.min(1, (GIF_MAX_SIDE * factor) / Math.max(cw, ch));
    const ow = Math.max(1, Math.round(cw * scale));
    const oh = Math.max(1, Math.round(ch * scale));

    const full = document.createElement("canvas");
    full.width = W;
    full.height = H;
    const fctx = full.getContext("2d", { willReadFrequently: true })!;
    const tmp = document.createElement("canvas");
    const tctx = tmp.getContext("2d")!;
    const out = document.createElement("canvas");
    out.width = ow;
    out.height = oh;
    const octx = out.getContext("2d", { willReadFrequently: true })!;

    const enc: any = GIFEncoder();
    let prev: any = null;
    let restore: ImageData | null = null;
    let pending = 0;

    for (let i = 0; i < frames.length; i++) {
      const f = frames[i];

      // undo the previous frame as its disposal method asks
      if (prev) {
        if (prev.disposalType === 2) {
          fctx.clearRect(
            prev.dims.left,
            prev.dims.top,
            prev.dims.width,
            prev.dims.height,
          );
        } else if (prev.disposalType === 3 && restore) {
          fctx.putImageData(restore, 0, 0);
        }
      }
      restore = f.disposalType === 3 ? fctx.getImageData(0, 0, W, H) : null;

      tmp.width = f.dims.width;
      tmp.height = f.dims.height;
      tctx.putImageData(
        new ImageData(new Uint8ClampedArray(f.patch), f.dims.width, f.dims.height),
        0,
        0,
      );
      fctx.drawImage(tmp, f.dims.left, f.dims.top);
      prev = f;

      pending += f.delay || 100;
      if (i % step !== 0) continue;

      octx.clearRect(0, 0, ow, oh);
      octx.drawImage(full, sx, sy, cw, ch, 0, 0, ow, oh);
      const rgba = octx.getImageData(0, 0, ow, oh).data;
      let alpha = false;
      for (let p = 3; p < rgba.length; p += 4) {
        if (rgba[p] < 255) {
          alpha = true;
          break;
        }
      }
      const format = alpha ? "rgba4444" : "rgb565";
      const palette = quantize(
        rgba,
        256,
        alpha ? { format, oneBitAlpha: true } : { format },
      );
      const index = applyPalette(rgba, palette, format);
      const ti = alpha ? palette.findIndex((c: number[]) => c[3] === 0) : -1;
      enc.writeFrame(index, ow, oh, {
        palette,
        delay: Math.max(20, pending),
        repeat: 0,
        transparent: ti >= 0,
        transparentIndex: ti >= 0 ? ti : 0,
      });
      pending = 0;

      if (i % 4 === 0) await tick(); // keep the page responsive
    }

    enc.finish();
    return {
      blob: new Blob([enc.bytes()], { type: "image/gif" }),
      width: ow,
      height: oh,
    };
  }

  let last = { blob: new Blob([]), width: cw, height: ch };
  for (const factor of GIF_SCALES) {
    last = await encodeAt(factor);
    if (!options.maxSize || last.blob.size <= options.maxSize) break;
  }
  if (options.maxSize && last.blob.size > options.maxSize) {
    throw new CropSizeError(last.blob.size, options.maxSize);
  }

  const file = new File([last.blob], sourceFile.name, { type: "image/gif" });
  const dataUrl = options.includeDataUrl
    ? await new Promise<string>((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(String(fr.result));
        fr.onerror = () => reject(fr.error);
        fr.readAsDataURL(last.blob);
      })
    : undefined;

  return { file, blob: last.blob, dataUrl, width: last.width, height: last.height };
}
